import { memo, useState, useEffect, useRef } from 'react';

interface PriceChangeCellProps {
  value: number | null | undefined;
  previousValue?: number | null;
  decimals?: number;
  currency?: string;
}

export const PriceChangeCell = memo(({
  value,
  previousValue,
  decimals = 2,
  currency = '₺'
}: PriceChangeCellProps) => {
  const [flashType, setFlashType] = useState<'positive' | 'negative' | null>(null);
  const prevValueRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (
      prevValueRef.current !== undefined &&
      prevValueRef.current !== null &&
      value !== null &&
      value !== undefined &&
      prevValueRef.current !== value
    ) {
      setFlashType(value > prevValueRef.current ? 'positive' : 'negative');
      const timer = setTimeout(() => setFlashType(null), 800);
      return () => clearTimeout(timer);
    }
    prevValueRef.current = value;
  }, [value]);

  // Also handle explicit previousValue prop
  useEffect(() => {
    if (
      previousValue !== undefined &&
      previousValue !== null &&
      value !== null &&
      value !== undefined &&
      previousValue !== value
    ) {
      setFlashType(value > previousValue ? 'positive' : 'negative');
      const timer = setTimeout(() => setFlashType(null), 800);
      return () => clearTimeout(timer);
    }
  }, [value, previousValue]);

  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const formattedValue = value.toLocaleString('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  const flashStyle = flashType
    ? {
        backgroundColor: flashType === 'positive'
          ? 'var(--color-positive-bg)'
          : 'var(--color-negative-bg)',
        color: flashType === 'positive'
          ? 'var(--color-positive)'
          : 'var(--color-negative)'
      }
    : {};

  return (
    <span
      className={flashType === 'positive' ? 'animate-flash-positive' : flashType === 'negative' ? 'animate-flash-negative' : ''}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        padding: '2px 6px',
        borderRadius: '4px',
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        fontSize: '13px',
        fontWeight: 500,
        transition: 'background-color 0.3s ease, color 0.3s ease',
        ...flashStyle
      }}
    >
      <span style={{ opacity: 0.7, fontSize: '11px' }}>{currency}</span>
      <span>{formattedValue}</span>
    </span>
  );
});

PriceChangeCell.displayName = 'PriceChangeCell';
